import React, { useEffect, useState } from "react";
import { RenderMarkdownWithLatex } from "../../utils/utils";

// --- IMPORTY MODALI ---
import ReportIssueModal from "./ReportIssueModal";
import CorrectionModal from "./CorrectionModal"; 
import AiChatPopup from "../molecules/AiChatPopup";
import ProblemManagerModal from '../../components/admin/ProblemManagerModal';
import { startChatAndGenerateSolution, continueChat } from "../../services/geminiService";
import SolutionStepper from '../molecules/SolutionStepper';
import HintAndFormulas from '../molecules/HintAndFormulas';


const apiUrl = process.env.REACT_APP_API_SOURCE;
const token = localStorage.getItem("token");

// --- KOMPONENTY POMOCNICZE ---

const SectionHeader = ({ title, icon, colorClass }) => (
  <h5 className={`text-xs font-bold uppercase tracking-wide mb-3 flex items-center gap-2 border-b pb-2 ${colorClass} border-opacity-20`}>
    {icon} {title}
  </h5>
);

const InfoBlock = ({ title, items, emptyMsg, colorTheme, renderItem }) => {
  const borderColor = `border-${colorTheme}-200`;
  const bgColor = `bg-${colorTheme}-50`;
  const textColor = `text-${colorTheme}-900`;

  return (
    <div className={`rounded-lg border ${borderColor} ${bgColor} p-4 h-full shadow-sm`}>
      <SectionHeader title={title} colorClass={textColor} />
      {items && items.length > 0 ? (
        <ul className="space-y-3">
          {items.map((item, idx) => (
            <li key={idx} className="text-sm text-gray-800 bg-white bg-opacity-60 p-2 rounded border border-gray-100 shadow-sm">
              {renderItem ? renderItem(item) : <RenderMarkdownWithLatex text={String(item)} />}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-gray-400 italic text-center py-2">{emptyMsg}</p>
      )}
    </div>
  );
};

// NOWY KOMPONENT DO POJEDYNCZEGO ZADANIA TRENINGOWEGO (Z PRZYCISKIEM KOPIOWANIA)
const TrainingProblemItem = ({ problem }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyRaw = () => {
    if (!problem.question) return;
    navigator.clipboard.writeText(problem.question);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between items-center text-xs font-bold text-blue-800 mb-1 border-b border-blue-100 pb-1">
        <span>ID: {problem.problem_id}</span>
        <div className="flex items-center gap-2">
            <span className="text-blue-500">ELO: {problem.problem_elo}</span>
            <button 
                onClick={handleCopyRaw} 
                title="Kopiuj surową treść do schowka (dla AI)"
                className="ml-1 px-1.5 py-0.5 bg-white border border-blue-200 rounded hover:bg-blue-50 text-blue-600 transition-all"
            >
                {copied ? "✅" : "📋"}
            </button>
        </div>
      </div>
      <div className="text-xs text-gray-600 line-clamp-3 hover:line-clamp-none cursor-pointer transition-all">
        <RenderMarkdownWithLatex text={problem.question || "..."} />
      </div>
    </div>
  );
};

const AnswerVariant = ({ label, content, isCorrect }) => (
    <div className={`p-3 rounded-md border ${isCorrect ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-200'} flex flex-col h-full`}>
        <span className={`text-xs font-bold uppercase mb-1 ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
            {label} {isCorrect ? '(Poprawna)' : '(Błędna)'}
        </span>
        <div className="text-sm text-gray-800 font-medium">
            <RenderMarkdownWithLatex text={content || "-"} />
        </div>
    </div>
);

const MaturaProblemCard = ({
  problem,
  onReportIssue,
  onCorrectIssueRequest,
  onAskAI,
  onManageProblem,
  onRemoveFromSheet
}) => {
  // --- NOWE STANY WIDOCZNOŚCI (Domyślnie TRUE dla wygody admina) ---
  const [showHint, setShowHint] = useState(true);
  const [showSolution, setShowSolution] = useState(true);

  const imageUrl = problem.image_link
    ? (problem.image_link.startsWith('http') ? problem.image_link : `${process.env.REACT_APP_STATIC_FILES_SOURCE || ''}/images/${problem.image_link}`)
    : null;

  const formulas = problem.linked_formulas || problem.matura_formula_name_raw || [];
  const trainingProblems = problem.linked_training_problems || [];
  const criteria = problem.criteria || [];

  return (
    <div className="relative border border-gray-300 bg-white rounded-lg p-0 mb-10 shadow-md hover:shadow-lg transition-shadow group">
      
      {/* --- NAGŁÓWEK --- */}
      <div className="bg-gray-50 border-b border-gray-200 p-4 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-xl font-bold shadow-sm">
            Zadanie {problem.problem_number}
          </div>
          <div className="flex flex-col">
             <span className="text-xs font-mono text-gray-400">DB ID: {problem.problem_id}</span>
             <div className="flex gap-2 mt-1">
                <span className="text-xs font-bold px-2 py-0.5 rounded bg-green-100 text-green-800 border border-green-200">
                   {problem.sheet_max_points} pkt
                </span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded border ${problem.sheet_is_open ? 'bg-purple-100 text-purple-800 border-purple-200' : 'bg-blue-100 text-blue-800 border-blue-200'}`}>
                   {problem.sheet_is_open ? "Otwarte" : "Zamknięte (ABCD)"}
                </span>
                <span className="text-xs font-bold px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">
                   ELO: {problem.problem_elo}
                </span>
             </div>
          </div>
        </div>

        {/* PANEL AKCJI */}
        <div className="flex gap-2">
          <button onClick={() => onManageProblem(problem)} className="px-3 py-1.5 bg-white border border-blue-300 text-blue-700 rounded hover:bg-blue-50 text-xs font-bold transition-colors shadow-sm">
            ✏️ Edytuj
          </button>
          <button onClick={() => onAskAI(problem)} className="px-3 py-1.5 bg-white border border-purple-300 text-purple-700 rounded hover:bg-purple-50 text-xs font-bold transition-colors shadow-sm">
            🤖 AI
          </button>
          <button onClick={() => onCorrectIssueRequest(problem)} className="px-3 py-1.5 bg-white border border-yellow-300 text-yellow-700 rounded hover:bg-yellow-50 text-xs font-bold transition-colors shadow-sm">
            🛠️ Korekta
          </button>
          <button onClick={() => onRemoveFromSheet(problem.sheet_problem_id)} className="px-3 py-1.5 bg-white border border-red-200 text-red-600 rounded hover:bg-red-50 text-xs font-bold transition-colors shadow-sm">
            🗑️
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* --- TREŚĆ ZADANIA --- */}
        <div className="mb-8">
          <div className="text-gray-900 leading-relaxed text-[1.1rem]">
             <RenderMarkdownWithLatex text={problem.question || "Brak treści pytania."} />
          </div>
          {imageUrl && (
            <div className="mt-4 flex justify-center bg-gray-50 p-2 rounded border border-gray-200">
              <img src={imageUrl} alt="Ilustracja" className="max-h-[400px] rounded" />
            </div>
          )}
        </div>

        {/* --- GRID POBOCZNY --- */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* ... (InfoBlocki pozostają bez zmian, ewentualnie formuły można wywalić stąd, skoro będą w HintAndFormulas, ale dla admina mogą zostać jako "surowe dane") ... */}
          
          <InfoBlock 
            title="Zadania Treningowe" 
            colorTheme="blue"
            items={trainingProblems} 
            emptyMsg="Brak podpiętych zadań."
            renderItem={(tp) => <TrainingProblemItem problem={tp} />}
          />

          <InfoBlock 
            title="Kryteria Oceniania" 
            colorTheme="red"
            items={criteria} 
            emptyMsg="Brak kryteriów (Zadanie Zamknięte lub brak danych)."
            renderItem={(crit) => (
              <div className="flex gap-2 items-start">
                <span className="font-bold text-white bg-red-500 px-1.5 py-0.5 rounded text-xs whitespace-nowrap shadow-sm">
                    {crit.points} pkt
                </span>
                <span className="text-xs text-gray-800 pt-0.5">
                    <RenderMarkdownWithLatex text={crit.description} />
                </span>
              </div>
            )}
          />
        </div>

        {/* --- WARIANTY ODPOWIEDZI (GRID) --- */}
        {!problem.sheet_is_open && (
             <div className="mb-8">
                <h5 className="text-xs font-bold uppercase tracking-wide mb-3 text-gray-500">Warianty Odpowiedzi (ABCD):</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <AnswerVariant label="Wariant Poprawny" content={problem.true_answer} isCorrect={true} />
                    {problem.false_answer1 && <AnswerVariant label="Dystraktor 1" content={problem.false_answer1} isCorrect={false} />}
                    {problem.false_answer2 && <AnswerVariant label="Dystraktor 2" content={problem.false_answer2} isCorrect={false} />}
                    {problem.false_answer3 && <AnswerVariant label="Dystraktor 3" content={problem.false_answer3} isCorrect={false} />}
                </div>
             </div>
        )}

        {/* --- ODPOWIEDZI OTWARTE I ROZWIĄZANIA (PREMIUM) --- */}
        <div className="flex flex-col gap-6 pt-6 border-t-2 border-gray-100 border-dashed">
          
          {/* Odpowiedź tekstowa (dla otwartych) */}
          {problem.sheet_is_open && problem.true_answer && (
            <div className="flex flex-col sm:flex-row sm:items-start gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="font-bold text-green-800 text-xs uppercase tracking-wider min-w-[120px] mt-1">
                  Modelowa Odp:
              </div>
              <div className="text-green-900 font-medium text-sm w-full">
                <RenderMarkdownWithLatex text={problem.true_answer} />
              </div>
            </div>
          )}

          {/* --- PODPOWIEDŹ + WZORY --- */}
          {(problem.hint || (problem.linked_formulas && problem.linked_formulas.length > 0)) && (
             <div>
                <button 
                    onClick={() => setShowHint(!showHint)}
                    className="text-xs uppercase font-bold text-gray-500 hover:text-gray-800 mb-2 flex items-center gap-2"
                >
                    {showHint ? "▼ Ukryj Podpowiedź i Wzory" : "▶ Pokaż Podpowiedź i Wzory"}
                </button>
                
                {showHint && (
                    <div className="border border-dashed border-gray-300 p-2 rounded bg-gray-50/30">
                        <HintAndFormulas 
                            hintText={problem.hint}
                            formulas={problem.linked_formulas} // Pamiętaj, że struktura musi pasować do oczekiwanej przez komponent
                        />
                    </div>
                )}
             </div>
          )}

          {/* --- ROZWIĄZANIE (STEPPER) --- */}
          {problem.solution && (
             <div>
                <button 
                    onClick={() => setShowSolution(!showSolution)}
                    className="text-xs uppercase font-bold text-blue-600 hover:text-blue-800 mb-2 flex items-center gap-2"
                >
                    {showSolution ? "▼ Ukryj Rozwiązanie (Stepper)" : "▶ Pokaż Rozwiązanie (Stepper)"}
                </button>
                
                {showSolution && (
                    <div className="border border-dashed border-blue-200 p-2 rounded bg-blue-50/10">
                        <SolutionStepper solutionText={problem.solution} />
                    </div>
                )}
             </div>
          )}
        </div>
      </div>
    </div>
  );
};


// --- GŁÓWNY KOMPONENT ADMINA ---

const MaturaSheetManager = () => {
  const [sheets, setSheets] = useState([]);
  const [selectedSheetId, setSelectedSheetId] = useState("");
  const [problems, setProblems] = useState([]);
  
  const [loadingSheets, setLoadingSheets] = useState(false);
  const [loadingProblems, setLoadingProblems] = useState(false);
  const [error, setError] = useState(null);

  // --- STANY DLA MODALI ---
  const [managingProblem, setManagingProblem] = useState(null);
  
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportingProblemData, setReportingProblemData] = useState(null);

  const [isCorrectionModalOpen, setIsCorrectionModalOpen] = useState(false);
  const [currentProblemForCorrection, setCurrentProblemForCorrection] = useState(null);
  const [aiCorrectedData, setAiCorrectedData] = useState(null);
  const [isRequestingCorrection, setIsRequestingCorrection] = useState(false);
  const [isAcceptingCorrection, setIsAcceptingCorrection] = useState(false);
  const [correctionError, setCorrectionError] = useState(null);

  const [isGeneralAiChatOpen, setIsGeneralAiChatOpen] = useState(false);
  const [currentProblemForGeneralAiChat, setCurrentProblemForGeneralAiChat] = useState(null);
  const [generalAiChatHistory, setGeneralAiChatHistory] = useState([]);
  const [isGeneralAiResponding, setIsGeneralAiResponding] = useState(false);
  const [problemSpecificChatHistories, setProblemSpecificChatHistories] = useState({});

  // --- EFEKTY ---

  useEffect(() => {
    if (token) {
        fetchSheets();
    } else {
        setError("Brak tokena. Zaloguj się.");
    }
  }, []);

  useEffect(() => {
    if (selectedSheetId) {
      fetchProblemsForSheet(selectedSheetId);
    } else {
      setProblems([]);
    }
  }, [selectedSheetId]);

  // --- API CALLS ---

  const fetchSheets = async () => {
    setLoadingSheets(true);
    try {
      const response = await fetch(`${apiUrl}matura/exam_sheets/`, {
        headers: { "Authorization": `Token ${token}` }
      });
      if (!response.ok) throw new Error("Błąd pobierania arkuszy");
      const data = await response.json();
      setSheets(data);
    } catch (e) {
      console.error(e);
      setError("Nie udało się pobrać listy arkuszy.");
    } finally {
      setLoadingSheets(false);
    }
  };

  const fetchProblemsForSheet = async (sheetId) => {
    setLoadingProblems(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}matura/exam_sheets/${sheetId}/problems/`, {
        headers: { "Authorization": `Token ${token}` }
      });
      if (!response.ok) throw new Error("Błąd pobierania zadań arkusza");
      const data = await response.json();
      
      const normalized = data.map(p => ({
        ...p,
        problem_translation_id: p.problem_translation_id || p.problem_id,
        matura_formula_name_raw: p.matura_formula_name || [], 
        matura_problem_source_raw: p.matura_problem_source || [],
        linked_training_problems: p.linked_training_problems || [], 
        criteria: p.criteria || [],
        linked_formulas: p.linked_formulas || [],
      }));
      
      setProblems(normalized);
    } catch (e) {
      console.error(e);
      setError("Nie udało się pobrać zadań dla tego arkusza.");
    } finally {
      setLoadingProblems(false);
    }
  };

  const handleRemoveFromSheet = async (sheetProblemId) => {
    if (!window.confirm("Czy na pewno chcesz usunąć to zadanie z arkusza? (Zadanie pozostanie w bazie danych)")) return;
    
    try {
        const response = await fetch(`${apiUrl}matura/exam_sheet_problem/${sheetProblemId}/delete/`, {
            method: 'DELETE',
            headers: { "Authorization": `Token ${token}` }
        });
        if (!response.ok) throw new Error("Błąd usuwania");
        
        setProblems(prev => prev.filter(p => p.sheet_problem_id !== sheetProblemId));
        alert("Zadanie odpięte od arkusza.");
    } catch(e) {
        alert("Wystąpił błąd: " + e.message);
    }
  };

  // --- HANDLERY DO MODALI ---

  const handleReportIssue = (pId, fId) => {
    setReportingProblemData({ problem_id: pId, field_id: fId });
    setIsReportModalOpen(true);
  };

  const handleCorrectIssueRequest = async (problem) => {
    // --- FIX: Używamy TYLKO problem_translation_id. Jeśli go nie ma, nie puszczamy requestu. ---
    const problemIdForCorrection = problem.problem_translation_id;
    
    if (!problemIdForCorrection) {
        alert("Błąd krytyczny: Brak powiązanego ID tłumaczenia (problem_translation_id) dla tego zadania. Sprawdź czy zadanie ma utworzone tłumaczenie w bazie.");
        return;
    }
    
    const userInstructions = window.prompt("Wprowadź instrukcje dla korekty AI:", "");
    if (userInstructions === null) return;

    setCurrentProblemForCorrection(problem);
    setIsRequestingCorrection(true);
    setCorrectionError(null);
    setAiCorrectedData(null);

    try {
        const payload = {
            problem_translation_id: problemIdForCorrection, // Teraz na pewno poprawne ID
            question: problem.question,
            true_answer: problem.true_answer,
            false_answer1: problem.false_answer1,
            false_answer2: problem.false_answer2,
            false_answer3: problem.false_answer3,
            hint: problem.hint,
            solution: problem.solution,
            context_level: "matura",
            user_instructions: userInstructions,
        };
        const response = await fetch(`${apiUrl}correct_problem_content/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            const errData = await response.json();
            throw new Error(errData.detail || "Błąd serwera AI");
        }
        const data = await response.json();
        setAiCorrectedData(data);
        setIsCorrectionModalOpen(true);
    } catch (e) {
        setCorrectionError(e.message);
        alert("Błąd korekty: " + e.message);
    } finally {
        setIsRequestingCorrection(false);
    }
  };

  const handleAcceptCorrection = async () => {
     if (!aiCorrectedData || !currentProblemForCorrection) return;
     const pId = currentProblemForCorrection.problem_translation_id || currentProblemForCorrection.problem_id;
     setIsAcceptingCorrection(true);
     try {
        const response = await fetch(`${apiUrl}problem_translation/${pId}/update_corrected/`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
            body: JSON.stringify(aiCorrectedData),
        });
        if (!response.ok) throw new Error("Błąd zapisu");
        fetchProblemsForSheet(selectedSheetId);
        setIsCorrectionModalOpen(false);
        setAiCorrectedData(null);
        alert("Zapisano zmiany.");
     } catch(e) {
         setCorrectionError(e.message);
     } finally {
         setIsAcceptingCorrection(false);
     }
  };

  const handleAskGeneralAI = (problem) => {
    setCurrentProblemForGeneralAiChat(problem);
    setIsGeneralAiChatOpen(true);
    const pId = problem.problem_translation_id || problem.problem_id;
    
    const history = problemSpecificChatHistories[pId] || [];
    setGeneralAiChatHistory(history);

    if(history.length === 0) {
        setIsGeneralAiResponding(true);
        const initMsg = `Zadanie Maturalne (ID ${problem.problem_id}). Treść: ${problem.question}. Odpowiedź: ${problem.true_answer}. Rozwiązanie: ${problem.solution}. Wyjaśnij mi to zadanie.`;
        
        startChatAndGenerateSolution(initMsg, null, problem.true_answer, problem.sheet_max_points, process.env.REACT_APP_GEMINI_API_KEY)
            .then(({ updatedHistory }) => {
                setGeneralAiChatHistory(updatedHistory);
                setProblemSpecificChatHistories(prev => ({...prev, [pId]: updatedHistory}));
            })
            .catch(err => console.error(err))
            .finally(() => setIsGeneralAiResponding(false));
    }
  };

  const handleSendGeneralChatMessage = async (msg) => {
      const pId = currentProblemForGeneralAiChat.problem_translation_id || currentProblemForGeneralAiChat.problem_id;
      const currentHist = generalAiChatHistory;
      const userMsg = { role: "user", parts: [{ text: msg }] };
      setGeneralAiChatHistory([...currentHist, userMsg]);
      setIsGeneralAiResponding(true);
      
      try {
          const { updatedHistory } = await continueChat(currentHist, msg, currentProblemForGeneralAiChat.sheet_max_points, process.env.REACT_APP_GEMINI_API_KEY);
          setGeneralAiChatHistory(updatedHistory);
          setProblemSpecificChatHistories(prev => ({...prev, [pId]: updatedHistory}));
      } catch(e) {
          console.error(e);
      } finally {
          setIsGeneralAiResponding(false);
      }
  };


  return (
    <div className="container mx-auto px-4 py-8 bg-gray-50 min-h-screen">
      {/* NAGŁÓWEK I WYBÓR ARKUSZA */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-indigo-100 mb-8">
        <div className="flex flex-col md:flex-row justify-between items-center border-b border-gray-100 pb-4 mb-4">
            <h1 className="text-2xl font-bold text-indigo-900">
               🏛️ Zarządzanie Arkuszami Maturalnymi
            </h1>
            <div className="text-sm text-gray-500">Panel Administratora</div>
        </div>
        
        {error && <p className="mb-4 text-red-600 bg-red-50 p-3 rounded border border-red-200">{error}</p>}
        {isRequestingCorrection && <p className="mb-4 text-blue-600 bg-blue-50 p-3 rounded animate-pulse">AI przetwarza korektę...</p>}

        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="w-full md:w-1/2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Wybierz Arkusz do Edycji</label>
            <select 
              value={selectedSheetId} 
              onChange={(e) => setSelectedSheetId(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              disabled={loadingSheets}
            >
              <option value="">-- Wybierz z listy --</option>
              {sheets.map(s => (
                <option key={s.sheet_id} value={s.sheet_id}>
                  {s.year} | {s.name} (Formuła {s.formula}) {s.is_visible ? '' : '[UKRYTY]'}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex gap-2">
             <button 
               className="px-5 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 shadow-sm transition-colors"
               onClick={() => fetchProblemsForSheet(selectedSheetId)}
               disabled={!selectedSheetId || loadingProblems}
             >
               {loadingProblems ? "Ładowanie..." : "Odśwież Listę"}
             </button>
             {selectedSheetId && (
                 <button 
                    onClick={() => alert("Funkcja 'Dodaj zadanie' w przygotowaniu.")}
                    className="px-5 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 shadow-sm transition-colors"
                 >
                    + Dodaj Zadanie
                 </button>
             )}
          </div>
        </div>
      </div>

      {/* LISTA ZADAŃ */}
      <div className="max-w-5xl mx-auto">
        {!selectedSheetId && (
            <div className="text-center py-20 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
                <p className="text-xl font-medium">Wybierz arkusz powyżej, aby rozpocząć pracę.</p>
            </div>
        )}

        {selectedSheetId && problems.length === 0 && !loadingProblems && (
            <div className="text-center py-20 text-gray-500 bg-white rounded-lg border border-dashed border-gray-300">
                <p className="text-lg">Ten arkusz nie ma jeszcze przypisanych żadnych zadań.</p>
            </div>
        )}

        {problems.map((problem) => (
            <MaturaProblemCard 
                key={problem.sheet_problem_id}
                problem={problem}
                onManageProblem={setManagingProblem}
                onRemoveFromSheet={handleRemoveFromSheet}
                onReportIssue={handleReportIssue}
                onCorrectIssueRequest={handleCorrectIssueRequest}
                onAskAI={handleAskGeneralAI}
            />
        ))}
      </div>

      {/* MODALE */}
      {managingProblem && (
        <ProblemManagerModal 
          problem={managingProblem}
          onClose={() => setManagingProblem(null)}
          onDataChange={() => fetchProblemsForSheet(selectedSheetId)}
        />
      )}

      {isReportModalOpen && reportingProblemData && (
        <ReportIssueModal 
            onClose={() => setIsReportModalOpen(false)} 
            reportIssueData={reportingProblemData} 
        />
      )}

      {isCorrectionModalOpen && currentProblemForCorrection && aiCorrectedData && (
        <CorrectionModal
          isOpen={isCorrectionModalOpen}
          onClose={() => setIsCorrectionModalOpen(false)}
          originalProblem={currentProblemForCorrection}
          correctedData={aiCorrectedData}
          onAcceptCorrection={handleAcceptCorrection}
          isAccepting={isAcceptingCorrection}
          error={correctionError}
        />
      )}

      {isGeneralAiChatOpen && currentProblemForGeneralAiChat && (
        <AiChatPopup
          isOpen={isGeneralAiChatOpen}
          onClose={() => setIsGeneralAiChatOpen(false)}
          chatTitle={`AI Chat: Zadanie ${currentProblemForGeneralAiChat.problem_number}`}
          chatHistory={generalAiChatHistory}
          onSendMessage={handleSendGeneralChatMessage}
          isResponding={isGeneralAiResponding}
          problemQuestion={currentProblemForGeneralAiChat.question}
        />
      )}
    </div>
  );
};

export default MaturaSheetManager;